import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Chip,
  Badge,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  PersonAdd as PersonAddIcon,
  UploadFile as UploadFileIcon,
  MailOutline as MailOutlineIcon,
  Chat as ChatIcon,
  Pin as PinIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Add as AddIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import useAdminChatUnread from '../hooks/useAdminChatUnread';

const DRAWER_WIDTH = 260;

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isAdmin = user?.role === 'admin';
  const { totalUnread: chatUnreadCount } = useAdminChatUnread(Boolean(isAdmin));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    const confirmLeaveIfUnsavedChanges = () => {
      const hasUnsavedChanges = Boolean((window as any).__courseBuilderHasUnsavedChanges);
      if (!hasUnsavedChanges) return true;
      return window.confirm('Il y a des modifications non sauvegardées. Quitter sans sauvegarder ?');
    };

    if (!confirmLeaveIfUnsavedChanges()) return;
    const hasUnsavedChanges = Boolean((window as any).__courseBuilderHasUnsavedChanges);
    if (hasUnsavedChanges && location.pathname === path) return;
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    const hasUnsavedChanges = Boolean((window as any).__courseBuilderHasUnsavedChanges);
    if (hasUnsavedChanges) {
      const shouldLeave = window.confirm(
        'Il y a des modifications non sauvegardées. Quitter sans sauvegarder ?'
      );
      if (!shouldLeave) return;
    }
    logout();
    handleProfileMenuClose();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Tableau de bord', icon: <DashboardIcon />, path: '/', roles: ['admin'] },
    { text: 'Formations', icon: <SchoolIcon />, path: '/courses', roles: ['admin'] },
    { text: 'Suivi apprenants', icon: <GroupsIcon />, path: '/tracking', roles: ['coach', 'entreprise'] },
    { text: 'Gestion des utilisateurs', icon: <PersonAddIcon />, path: '/users', roles: ['admin'] },
    { text: 'Import apprenants', icon: <UploadFileIcon />, path: '/students/import', roles: ['admin'] },
    { text: 'Message apprenant', icon: <MailOutlineIcon />, path: '/student-messages', roles: ['admin'] },
    { text: 'Chat apprenants', icon: <ChatIcon />, path: '/chat', roles: ['admin'], badge: 'chat' as const },
    { text: 'Entreprises', icon: <BusinessIcon />, path: '/companies', roles: ['admin'] },
    { text: 'Codes de connexion', icon: <PinIcon />, path: '/verification-codes', roles: ['admin'] },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(user?.role || '')
  );

  const drawer = (
    <Box sx={{ bgcolor: '#281e30', height: '100%' }}>
      <Toolbar sx={{ px: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="center" width="100%">
          <img 
            src={`${process.env.PUBLIC_URL}/logo_fasterclass.svg`}
            alt="Fasterclass Logo" 
            style={{ width: '80%', height: 'auto', maxHeight: '40px' }}
            onError={(e) => {
              console.error('Erreur chargement logo');
              e.currentTarget.style.display = 'none';
            }}
          />
        </Box>
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      <List sx={{ px: 2 }}>
        {filteredMenuItems.map((item) => {
          const isActive =
            location.pathname === item.path ||
            (item.path === '/student-messages' && location.pathname.startsWith('/student-messages')) ||
            (item.path === '/verification-codes' && location.pathname.startsWith('/verification-codes')) ||
            (item.path === '/chat' && location.pathname.startsWith('/chat')) ||
            (item.path === '/tracking' && (
              location.pathname.startsWith('/tracking') ||
              location.pathname.includes('/students')
            ));
          return (
          <ListItem 
            key={item.text}
            onClick={() => handleNavigation(item.path)}
            sx={{
              cursor: 'pointer',
              borderRadius: 2,
              mb: 0.5,
              backgroundColor: isActive ? 'rgba(73, 255, 193, 0.15)' : 'transparent',
              '&:hover': {
                backgroundColor: isActive ? 'rgba(73, 255, 193, 0.25)' : 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            <ListItemIcon sx={{ 
              color: isActive ? '#49ffc1' : 'rgba(255, 255, 255, 0.7)',
              minWidth: 40
            }}>
              {'badge' in item && item.badge === 'chat' && chatUnreadCount > 0 ? (
                <Badge badgeContent={chatUnreadCount > 99 ? '99+' : chatUnreadCount} color="error">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
            </ListItemIcon>
            <ListItemText 
              primary={item.text} 
              sx={{ 
                '& .MuiListItemText-primary': { 
                  color: isActive ? '#49ffc1' : '#fff',
                  fontWeight: isActive ? 700 : 400,
                  fontSize: '0.95rem'
                } 
              }}
            />
          </ListItem>
        );})}
      </List>
      <Divider sx={{ mx: 2, my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
      <Box px={3} pb={2}>
        {user?.role === 'admin' && (
          <>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 1, display: 'block' }} fontWeight="500">
              Actions rapides
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              <Chip
                label="Nouvelle formation"
                icon={<AddIcon sx={{ color: '#281e30' }} />}
                clickable
                size="small"
                onClick={() => handleNavigation('/courses/new')}
                sx={{
                  justifyContent: 'flex-start',
                  bgcolor: '#49ffc1',
                  color: '#281e30',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: '#3de6ad',
                  },
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          bgcolor: '#281e30',
          color: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }}
      >
        <Toolbar>
          <IconButton
            sx={{ mr: 2, display: { md: 'none' }, color: '#fff' }}
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip
              label={user?.role?.toUpperCase()}
              size="small"
              sx={{
                bgcolor: '#49ffc1',
                color: '#281e30',
                fontWeight: 700,
                border: 'none',
              }}
            />
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, color: '#fff' }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <IconButton onClick={handleProfileMenuOpen}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#49ffc1', color: '#281e30', fontWeight: 700 }}>
                {user?.firstName?.charAt(0)}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => { handleNavigation('/profile'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          Profil
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Déconnexion
        </MenuItem>
      </Menu>

      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              bgcolor: '#281e30',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              bgcolor: '#281e30',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: '64px',
          minHeight: 'calc(100vh - 64px)',
          bgcolor: 'background.default',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout; 